import { asyncDelay } from '@/services/asyncHelpers';
import { pushAlert } from '@/services/messages/alertService';
import { Blob } from 'sigma-mappings';
import callRefreshIfNeeded from '@/services/http/interceptors/refreshInterceptor';
import store from '@/store';
import axiosFactory from '../axiosFactory';
import { isRetryableResponse } from '../common';

const DEFAULT_INTERVAL = 10000;
const MAX_ERROR_RETRIES = 2;

function getMaxRetries(options) {
  return options.maxRetries != null ? options.maxRetries : MAX_ERROR_RETRIES;
}

function readBlob(blob) {
  const reader = new FileReader();
  return new Promise((resolve) => {
    reader.onload = () => resolve(reader.result);
    reader.readAsText(blob);
  });
}

async function getGuidFromResponseData(data) {
  const blobConvert = await readBlob(data);
  const object = JSON.parse(blobConvert);

  if (typeof object === 'string') {
    return object;
  }

  const { message, id } = new Blob(object);

  if (id && !message) {
    return id;
  }

  if (id && message) {
    pushAlert({
      message,
      type: 'warning',
    });
    return id;
  }

  throw new Error(
    'Não foi possível recuperar o GUID da requisição assincrona.',
  );
}

function isResponseToWait(response) {
  return response.status === 202;
}

function getHeaders(config) {
  const newToken = `Bearer ${store.getters.token}`;

  const { headers } = config;
  headers.Authorization = newToken ?? headers.Authorization;
  return headers;
}

async function refreshToken(instance, error) {
  const originalRequest = error.config;

  if (
    error.response
    && error.response.status === 401
    && !originalRequest.customRetry
  ) {
    originalRequest.customRetry = true;

    try {
      await callRefreshIfNeeded(error.response);
      originalRequest.headers = getHeaders(error.config);
      return instance(originalRequest);
    } catch (refreshError) {
      throw refreshError;
    }
  }

  return Promise.reject(error);
}

export default async function asyncInterceptor(options, response = {}) {
  if (!isResponseToWait(response)) {
    return response;
  }

  const guid = await getGuidFromResponseData(response.data);
  // Gera nova url com guid
  const originalUrl = response.config.$originalUrl || response.config.url;
  const url = `${originalUrl}/${guid}`;

  const instance = axiosFactory();
  instance.interceptors.response.use(
    innerResponse => innerResponse,
    async error => refreshToken(instance, error),
  );

  let errorRetries = 0;
  let errorResponse;
  const setLastError = (asyncError) => {
    if (
      !isRetryableResponse(asyncError.response)
      || errorRetries >= getMaxRetries(options)
    ) {
      errorResponse = asyncError;
    }
    errorRetries += 1;
  };

  let successfulResponse;
  const setSuccessfulResponse = (asyncResponse) => {
    errorRetries = 0;
    if (!isResponseToWait(asyncResponse)) {
      successfulResponse = asyncResponse;
    }
  };

  // Desabilitando o 'no-await-in-loop' pois o loop não é para processamento, e sim para esperar
  // de maneira repetitiva.
  do {
    // eslint-disable-next-line no-await-in-loop
    await asyncDelay(options.interval || DEFAULT_INTERVAL);
    const headers = getHeaders(response.config);
    // eslint-disable-next-line no-await-in-loop
    await instance
      .get(url, {
        ...response.config,
        ...{ headers },
        url,
        method: 'GET',
      })
      .then(setSuccessfulResponse)
      .catch(setLastError);
  } while (!successfulResponse && !errorResponse);

  if (successfulResponse) {
    return successfulResponse;
  }

  return Promise.reject(errorResponse);
}
